import { Component, CUSTOM_ELEMENTS_SCHEMA, effect, inject } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PlannerViewsStateService } from '../../../../services/planner-views-state.service';
import { CustomRouteService } from 'src/app/pages/custom-route/services/custom-route.service';
import { MatDialogService } from 'src/app/services/mat-dialog.service';
import { EditRouteService } from '../../services/edit-route.service';
import { HttpErrorResponse } from '@angular/common/http';
import { PlannerDayStateService } from '../../../../services';
import { CustomEditedRouteResponse } from '../../interfaces/edit-route.response';
import { openCiSnackbar } from '@iurban-malaga/cicerone-ui';
import { StandardDialog } from 'src/app/shared/dialogs';

@Component({
  selector: 'app-action-buttons',
  standalone: true,
  imports: [
    TranslateModule
  ],
  templateUrl: './action-buttons.component.html',
  styleUrls: ['./action-buttons.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ActionButtonsComponent {
  // Service injections
  private _translateService = inject(TranslateService);
  private _customRouteService = inject(CustomRouteService);
  private _editRouteService = inject(EditRouteService);
  private _plannerViewsStateService = inject(PlannerViewsStateService);
  private _plannerDayStateService = inject(PlannerDayStateService);
  private _matDialogService = inject(MatDialogService);
  
  // State service signals
  private _customRouteEditedDataToRender = this._editRouteService.customRouteEditedDataToRender;
  private _initialStateCustomRouteEditedDataToRender = this._editRouteService.initialStateCustomRouteEditedDataToRender;
  private _dayToShowIndex = this._plannerDayStateService.dayToShowIndex;
  
  // Local properties
  public disabledButton: boolean = false;
  
  // Effects
  updateButtonStateAccordingToChanges = effect(() => {
    const initialState = this._initialStateCustomRouteEditedDataToRender();
    const currentState = this._customRouteEditedDataToRender();
    
    if (!initialState || !currentState) {
      this.disabledButton = true; 
      return;
    } 

    this.disabledButton = JSON.stringify(initialState) === JSON.stringify(currentState);
  }, { allowSignalWrites: true });  
  
  // Methods
  public handleCancelEditClick() {
    this._openCloseConfirmationDialog();
  }
  
  public handleUpdateEditClick() {
    this._sendNewEditedCustomRouteData();
  }
  
  private _sendNewEditedCustomRouteData(): void {
    this._customRouteService.setIsLoading(true);
    
    this._editRouteService.sendNewEditedCustomRouteData(this._dayToShowIndex())
      .subscribe({
        next: (response: CustomEditedRouteResponse) => {
          this._customRouteService.setIsLoading(false);

          // Handle successes/errors in the response UX 
          if (response.error && response.error.length > 0) {
            this._openSuccessErrorListUpdateCustomRouteDialog(response);
          } else {
            this._redirectToPlannerView();
            this._openSuccessUpdateCustomRouteSnackbar();
          }
        },
        error: (error: HttpErrorResponse) => {
          this._customRouteService.setIsLoading(false);
          this._openServerErrorUpdateCustomRouteDialog();
          console.error(`Error updating custom route data:`, error);
        }
      });
  }
  
  private _openServerErrorUpdateCustomRouteDialog(): void {
    const dialogRef = this._matDialogService.openMatDialog({
      component: StandardDialog,
      data: {
        title: this._translateService.instant('Error en el servidor'),
        content: this._translateService.instant('Ha ocurrido un error') + '. ' + this._translateService.instant('Inténtelo de nuevo más tarde'),
        primaryActionButtonText: this._translateService.instant('Volver a intentarlo'),
        handleClickPrimaryButton: () => {
          this._matDialogService.closeMatDialog(dialogRef);
        }
      }
    });
  }
  
  private _openSuccessErrorListUpdateCustomRouteDialog(response: CustomEditedRouteResponse): void {
    const dialogRef = this._matDialogService.openMatDialog({
      component: StandardDialog,
      data: {
        content: { errorList: response.error, successList: response.success },
        primaryActionButtonText: this._translateService.instant('Entendido'),
        secondaryActionButtonText: this._translateService.instant('Ver registro'),
        handleClickPrimaryButton: () => {
          this._matDialogService.closeMatDialog(dialogRef);
        },
        handleClickSecondaryButton: () => {
          this._editRouteService.setIsEditRouteRegisterOpen(true);
          this._matDialogService.closeMatDialog(dialogRef);
        }
      }
    });
  }
  
  private _openCloseConfirmationDialog(): void {
    const dialogRef = this._matDialogService.openMatDialog({
      component: StandardDialog,
      data: {
        title: '¿' + this._translateService.instant('Quieres volver atrás') + '?',
        content: this._translateService.instant('Si regresas, perderás todos los cambios que hiciste en tu ruta. ¿Seguro que deseas continuar?'),
        primaryActionButtonText: this._translateService.instant('Sí, volver'),
        secondaryActionButtonText: this._translateService.instant('No, seguir aquí'),
        handleClickPrimaryButton: () => {
          this._editRouteService.cancelSuggestedPointsRequests();
          this._redirectToPlannerView();
          this._matDialogService.closeMatDialog(dialogRef);
        },
        handleClickSecondaryButton: () => {
          this._matDialogService.closeMatDialog(dialogRef);
        }
      }
    })
  }
  
  private _openSuccessUpdateCustomRouteSnackbar() {
    openCiSnackbar({
      content: this._translateService.instant('Ruta actualizada correctamente'),
      status: 'success',
      'icon-name': 'success',
      'horizontal-position': 'end',
      'vertical-position': 'bottom',
    })
  } 
  
  private _redirectToPlannerView() {
    this._plannerViewsStateService.isEditRouteView.set(false);
  }
}
